"use client";

import { useEffect } from 'react';
import { FEATURE_FLAGS } from '@/utils/config/featureFlags';
import AppUpdatePrompt from '@/components/common/AppUpdatePrompt';
import {
  BRAND_THEME_STORAGE_KEY,
  safeLocalStorageGetItem,
  safeLocalStorageKey,
  safeLocalStorageLength,
  safeLocalStorageRemoveItem,
} from '@/utils/storage/safeLocalStorage';
import { forgetRememberedOrganization } from '@/utils/organizations/rememberedOrganization';

// REQUIREMENTS SUMMARY:
// - Added logic to force the default brand theme (v2) if the theme switcher feature flag is disabled.
// - Cleans up any saved theme preference if the feature is disabled.
// - Mounts the global app update prompt so long-running browser tabs can discover new CheerVue deployments.
// - Treats blocked browser storage as "no saved preference" so auth pages can
//   still load on Mobile Safari/private contexts.

export default function ClientInitializer() {
  // Initialize the app with Supabase data source
  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('ClientInitializer running...');
      
      // Initialize brand theme from localStorage (default to v2 - CheerVue Default)
      // If the brand theme switcher is disabled, we always force v2
      const savedBrandTheme = safeLocalStorageGetItem(BRAND_THEME_STORAGE_KEY);
      
      if (FEATURE_FLAGS.ENABLE_BRAND_THEME_SWITCHER && savedBrandTheme === 'v1') {
        // Add brand-v1 for blue theme when feature enabled AND user explicitly chose v1
        document.documentElement.classList.add('brand-v1');
      } else {
        // Default is maroon (no class). Remove brand-v1 for v2 or when switcher disabled.
        document.documentElement.classList.remove('brand-v1');
        
        // If switcher is disabled, clean up any saved v1 preference
        if (!FEATURE_FLAGS.ENABLE_BRAND_THEME_SWITCHER && savedBrandTheme === 'v1') {
          safeLocalStorageRemoveItem(BRAND_THEME_STORAGE_KEY);
          console.log('ClientInitializer: Removed saved v1 theme preference as feature is disabled');
        }
      }
      
      // Add debug information
      console.log('ClientInitializer: Current localStorage values:');
      console.log('- dataSource:', safeLocalStorageGetItem('dataSource'));
      console.log('- currentOrganizationId:', safeLocalStorageGetItem('currentOrganizationId'));
      console.log('- brand-theme:', savedBrandTheme || 'v2 (default)');
      
      // Check for all auth-related items
      try {
        const authItems: string[] = [];
        for (let i = 0; i < safeLocalStorageLength(); i++) {
          const key = safeLocalStorageKey(i);
          if (key && (key.includes('auth') || key.includes('user') || key.includes('token') || key.includes('session'))) {
            authItems.push(key);
            console.log(`- Found auth-related item: ${key}`);
          }
        }
        
        // Add a global helper for clearing auth data (for debugging only)
        if (process.env.NODE_ENV === 'development') {
          (window as any).clearAuthData = () => {
            console.log('Clearing all auth-related localStorage data...');
            
            // Clear all auth-related items
            authItems.forEach(key => {
              safeLocalStorageRemoveItem(key);
              console.log(`Removed: ${key}`);
            });
            
            // Also clear organization data. This is the wipe-everything debug
            // path, so it takes the gym list too — unlike the callers that are
            // only dropping a gym the current user cannot use.
            forgetRememberedOrganization({ includeOrganizationList: true });
            console.log('Removed: currentOrganizationId');
            
            // Clear other potential auth data
            safeLocalStorageRemoveItem('dataSource');
            
            alert('Auth data cleared. The page will now reload.');
            window.location.href = '/signin';
          };
          
          console.log('Development helper available: call window.clearAuthData() in the console to clear auth data');
        }
      } catch (storageError) {
        console.warn('LocalStorage access failed in ClientInitializer:', storageError);
      }
    }
  }, []);

  return <AppUpdatePrompt />;
}
